// Import supabase untuk mendapatkan token dari session
import { supabase } from './supabase';

// Use relative path for production (Netlify Functions), absolute URL for development
// In production, use empty string because all endpoints already include '/api/'
// Netlify redirect will handle /api/* to the function
const API_URL = process.env.REACT_APP_API_URL || 
  (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5001');

const getAuthHeaders = async () => {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;

  return {
    'Content-Type': 'application/json',
    'Authorization': token ? `Bearer ${token}` : '',
  };
};

// Helper function to add timeout to fetch
const fetchWithTimeout = async (url, options = {}, timeout = 10000) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  
  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
    });
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new Error('Request timeout');
    }
    throw error;
  }
};

export const api = {
  async get(endpoint, options = {}) {
    const { timeout = 10000 } = options;
    const headers = await getAuthHeaders();
    const response = await fetchWithTimeout(`${API_URL}${endpoint}`, {
      headers,
    }, timeout);
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: response.statusText }));
      const errorObj = new Error(error.error || response.statusText);
      errorObj.response = { data: error, status: response.status };
      throw errorObj;
    }
    return response.json();
  },

  async post(endpoint, data, options = {}) {
    const { timeout = 10000 } = options;
    const headers = await getAuthHeaders();
    const response = await fetchWithTimeout(`${API_URL}${endpoint}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(data),
    }, timeout);
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: response.statusText }));
      const errorObj = new Error(error.error || response.statusText);
      errorObj.response = { data: error, status: response.status };
      throw errorObj;
    }
    return response.json();
  },

  async patch(endpoint, data) {
    const headers = await getAuthHeaders();
    const response = await fetchWithTimeout(`${API_URL}${endpoint}`, {
      method: 'PATCH',
      headers,
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || response.statusText);
    }
    return response.json();
  },

  async put(endpoint, data) {
    const headers = await getAuthHeaders();
    const response = await fetchWithTimeout(`${API_URL}${endpoint}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || response.statusText);
    }
    return response.json();
  },

  async delete(endpoint) {
    const headers = await getAuthHeaders();
    const response = await fetchWithTimeout(`${API_URL}${endpoint}`, {
      method: 'DELETE',
      headers,
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || response.statusText);
    }
    // Handle empty response
    const text = await response.text();
    return text ? JSON.parse(text) : {};
  },
};

